/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Withdrawals Hooks - React Query for Withdrawals
 * ============================================================================
 *
 * Custom hooks for withdrawal data fetching and mutations using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { withdrawalsService } from '@/services/superadmin/withdrawals.service';
import { Withdrawal, WithdrawalFilterParams, WithdrawalActionRequest } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const withdrawalKeys = {
  all: ['withdrawals'] as const,
  lists: () => [...withdrawalKeys.all, 'list'] as const,
  list: (params?: PaginationParams & WithdrawalFilterParams) =>
    [...withdrawalKeys.lists(), params] as const,
  details: () => [...withdrawalKeys.all, 'detail'] as const,
  detail: (id: string) => [...withdrawalKeys.details(), id] as const,
  stats: () => [...withdrawalKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch paginated withdrawals list
 */
export function useWithdrawals(params?: PaginationParams & WithdrawalFilterParams) {
  return useQuery<PaginatedResponse<Withdrawal>, ApiError>({
    queryKey: withdrawalKeys.list(params),
    queryFn: () => withdrawalsService.getWithdrawals(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single withdrawal detail
 */
export function useWithdrawal(id: string) {
  return useQuery<Withdrawal, ApiError>({
    queryKey: withdrawalKeys.detail(id),
    queryFn: () => withdrawalsService.getWithdrawal(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch withdrawal statistics
 */
export function useWithdrawalStats() {
  return useQuery<
    {
      pendingCount: number;
      pendingAmount: number;
      approvedCount: number;
      paidThisMonth: number;
    },
    ApiError
  >({
    queryKey: withdrawalKeys.stats(),
    queryFn: () => withdrawalsService.getWithdrawalStats(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for approving withdrawal
 */
export function useApproveWithdrawal() {
  const queryClient = useQueryClient();

  return useMutation<Withdrawal, ApiError, { id: string; data?: WithdrawalActionRequest }>({
    mutationFn: ({ id, data }) => withdrawalsService.approveWithdrawal(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.stats() });
    },
  });
}

/**
 * Hook for rejecting withdrawal
 */
export function useRejectWithdrawal() {
  const queryClient = useQueryClient();

  return useMutation<Withdrawal, ApiError, { id: string; data?: WithdrawalActionRequest }>({
    mutationFn: ({ id, data }) => withdrawalsService.rejectWithdrawal(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.stats() });
    },
  });
}

/**
 * Hook for marking withdrawal as paid
 */
export function useMarkWithdrawalPaid() {
  const queryClient = useQueryClient();

  return useMutation<Withdrawal, ApiError, { id: string; data?: WithdrawalActionRequest }>({
    mutationFn: ({ id, data }) => withdrawalsService.markWithdrawalPaid(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: withdrawalKeys.stats() });
    },
  });
}
